import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Trophy, Target, CheckCircle2, Code2, Award, Loader2, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useProfile } from "@/hooks/useProfile";
import { ChallengeCertificatePreview } from "./ChallengeCertificatePreview";

type Category = "power_bi" | "sql" | "python";

interface CategoryStats {
  total: number;
  completed: number;
  percentage: number;
  eligible: boolean;
}

interface ChallengesHeaderProps {
  categoryStats: Record<Category, CategoryStats>;
  globalStats: { total: number; completed: number; percentage: number };
}

const categoryLabels: Record<Category, string> = {
  python: "Python",
  sql: "SQL",
  power_bi: "Power BI (DAX)",
};

const MIN_ACCURACY = 80;

export function ChallengesHeader({ categoryStats, globalStats }: ChallengesHeaderProps) {
  const categories: Category[] = ["python", "sql", "power_bi"];
  const [generatingCert, setGeneratingCert] = useState<Category | null>(null);
  const [previewingCert, setPreviewingCert] = useState<Category | null>(null);
  const { profile } = useProfile();
  const isAdmin = profile?.is_admin === true;
  const [certificatePreview, setCertificatePreview] = useState<{
    certificate: any;
    userName: string;
    certificateTitle: string;
  } | null>(null);

  const handleGenerateCertificate = async (cat: Category) => {
    setGeneratingCert(cat);
    try {
      const { data, error } = await supabase.functions.invoke("generate-challenge-certificate", {
        body: { category: cat },
      });

      if (error) throw error;

      if (data?.success) {
        setCertificatePreview({
          certificate: data.certificate,
          userName: data.certificate.user_name,
          certificateTitle: data.certificate.certificate_title,
        });
        if (data.message !== 'Certificate already issued') {
          toast.success("Certificado emitido com sucesso!");
        }
      } else {
        toast.error(data?.error || "Erro ao gerar certificado");
      }
    } catch (err) {
      console.error("Certificate generation error:", err);
      toast.error("Erro ao gerar certificado. Tente novamente.");
    } finally {
      setGeneratingCert(null);
    }
  };

  const handleAdminPreview = async (cat: Category) => {
    setPreviewingCert(cat);
    try {
      const { data, error } = await supabase.functions.invoke("generate-challenge-certificate", {
        body: { category: cat, admin_preview: true },
      });
      if (error) throw error;
      if (data?.success) {
        setCertificatePreview({
          certificate: data.certificate,
          userName: data.certificate.user_name,
          certificateTitle: data.certificate.certificate_title,
        });
      } else {
        toast.error(data?.error || "Erro ao gerar preview");
      }
    } catch (err) {
      console.error("Admin preview error:", err);
      toast.error("Erro ao gerar preview do certificado.");
    } finally {
      setPreviewingCert(null);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Hero */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5">
                <Code2 className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 space-y-1">
                <h1 className="text-xl font-bold text-foreground">Desafios de Código</h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Pratique suas habilidades em <strong>Python</strong>, <strong>SQL</strong> e <strong>DAX (Power BI)</strong> com desafios
                  práticos de diferentes níveis de dificuldade. Seu código é avaliado automaticamente com feedback detalhado.
                </p>
              </div>
            </div>

            {/* Achievements info */}
            <div className="flex items-center gap-2 rounded-lg bg-purple-500/10 border border-purple-500/20 px-3 py-2">
              <Award className="h-4 w-4 text-purple-600 shrink-0" />
              <p className="text-xs text-purple-700 dark:text-purple-400">
                Cada desafio concluído também conta para suas <strong>conquistas</strong> e acumula pontos no ranking de gamificação.
              </p>
            </div>

            {/* Certification rule */}
            <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
              <Trophy className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Complete os desafios com pelo menos <strong>{MIN_ACCURACY}% de aproveitamento</strong> em cada categoria para conquistar seu certificado.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Category progress cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {categories.map((cat) => {
            const stats = categoryStats[cat];
            const isEligible = stats.eligible;
            const isGenerating = generatingCert === cat;

            return (
              <Card key={cat} className={`transition-colors ${isEligible ? "border-emerald-500/30 bg-emerald-500/5" : ""}`}>
                <CardContent className="pt-4 pb-4 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{categoryLabels[cat]}</span>
                    {isEligible ? (
                      <Badge className="text-[10px] bg-emerald-500/20 text-emerald-600 border-emerald-500/30 gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Apto
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] gap-1">
                        <Target className="h-3 w-3" />
                        {MIN_ACCURACY}%
                      </Badge>
                    )}
                  </div>
                  <Progress value={stats.percentage} className="h-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{stats.completed} de {stats.total} concluídos</span>
                    <span className={`font-semibold ${stats.percentage >= MIN_ACCURACY ? "text-emerald-600" : "text-foreground"}`}>
                      {stats.percentage}%
                    </span>
                  </div>
                  {isEligible && (
                    <Button
                      size="sm"
                      className="w-full mt-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                      onClick={() => handleGenerateCertificate(cat)}
                      disabled={isGenerating}
                    >
                      {isGenerating ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Award className="h-3 w-3 mr-1" />
                      )}
                      {isGenerating ? "Gerando..." : "Emitir Certificado"}
                    </Button>
                  )}
                  {isAdmin && !isEligible && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-1 text-xs h-8 border-primary/30 text-primary"
                      onClick={() => handleAdminPreview(cat)}
                      disabled={previewingCert === cat}
                    >
                      {previewingCert === cat ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Eye className="h-3 w-3 mr-1" />
                      )}
                      {previewingCert === cat ? "Gerando..." : "Pré-visualizar Certificado"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {certificatePreview && (
        <ChallengeCertificatePreview
          certificate={certificatePreview.certificate}
          userName={certificatePreview.userName}
          certificateTitle={certificatePreview.certificateTitle}
          onClose={() => setCertificatePreview(null)}
        />
      )}
    </>
  );
}
